import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  ClipboardList,
  Loader2,
  Lock,
  Play,
  Undo2,
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  Clock,
  UserCheck,
  X,
} from 'lucide-react';
import { WORKFLOW_KEYS } from '../../data/workflowChecklistTemplates';
import { ensureWorkflow } from '../../services/workflow/WorkflowEngine';
import {
  listTasks,
  completeTask,
  completionPercent,
  setTaskInProgress,
  reopenTask,
  addTaskNote,
} from '../../services/workflow/TaskService';
import { acerSlaFromAdmission } from '../../services/workflow/SLAEngine';
import { TASK_STATUS } from '../../workflow/taskModel';

// ── Inline confirmation dialog ──────────────────────────────────────────

function ConfirmBanner({ message, confirmLabel, confirmColor = 'bg-amber-600 hover:bg-amber-700', onConfirm, onCancel, children }) {
  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 animate-in fade-in">
      <p className="text-xs text-amber-800 mb-2">{message}</p>
      {children}
      <div className="mt-2 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
        <button type="button" onClick={onConfirm} className={`rounded-lg px-3 py-1 text-xs font-semibold text-white shadow-sm ${confirmColor}`}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

// ── Single task row ─────────────────────────────────────────────────────

const STATUS_THEME = {
  [TASK_STATUS.COMPLETED]: {
    border: 'border-emerald-200',
    bg: 'bg-gradient-to-r from-emerald-50 to-white',
    strip: 'bg-gradient-to-b from-emerald-400 to-emerald-600',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    iconSize: 'h-6 w-6',
    titleColor: 'text-slate-700',
  },
  [TASK_STATUS.BLOCKED]: {
    border: 'border-amber-200',
    bg: 'bg-gradient-to-r from-amber-50/60 to-white',
    strip: 'bg-gradient-to-b from-amber-400 to-amber-600',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    iconSize: 'h-5 w-5',
    titleColor: 'text-slate-900',
  },
  [TASK_STATUS.OVERDUE]: {
    border: 'border-red-200',
    bg: 'bg-gradient-to-r from-red-50/60 to-white',
    strip: 'bg-gradient-to-b from-red-400 to-red-600',
    iconBg: 'bg-red-100',
    iconText: 'text-red-600',
    iconSize: 'h-5 w-5',
    titleColor: 'text-slate-900',
  },
  [TASK_STATUS.IN_PROGRESS]: {
    border: 'border-blue-200',
    bg: 'bg-gradient-to-r from-blue-50/60 to-white',
    strip: 'bg-gradient-to-b from-blue-400 to-blue-600',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    iconSize: 'h-5 w-5',
    titleColor: 'text-slate-900',
  },
  [TASK_STATUS.CANCELLED]: {
    border: 'border-slate-200',
    bg: 'bg-slate-50/50',
    strip: 'bg-slate-300',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-400',
    iconSize: 'h-5 w-5',
    titleColor: 'text-slate-500',
  },
  _default: {
    border: 'border-slate-200',
    bg: 'bg-white',
    strip: 'bg-slate-300',
    iconBg: 'bg-slate-50',
    iconText: 'text-slate-400',
    iconSize: 'h-5 w-5',
    titleColor: 'text-slate-900',
  },
};

const STATUS_ICON_MAP = {
  [TASK_STATUS.COMPLETED]: CheckCircle2,
  [TASK_STATUS.BLOCKED]: Lock,
  [TASK_STATUS.OVERDUE]: AlertTriangle,
  [TASK_STATUS.IN_PROGRESS]: Play,
  [TASK_STATUS.CANCELLED]: X,
};

function TaskRow({ task, onComplete, onStartProgress, onReopen, onAddNote, currentUserId }) {
  const done = task.status === TASK_STATUS.COMPLETED;
  const blocked = task.status === TASK_STATUS.BLOCKED;
  const overdue = task.status === TASK_STATUS.OVERDUE;
  const cancelled = task.status === TASK_STATUS.CANCELLED;
  const isActionable = !done && !blocked && !cancelled;

  const [expanded, setExpanded] = useState(false);
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [evidenceNote, setEvidenceNote] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showReopenForm, setShowReopenForm] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const handleSubmitComplete = () => {
    onComplete(task.id, evidenceNote.trim() || undefined);
    setShowCompleteForm(false);
    setEvidenceNote('');
  };

  const handleSubmitNote = () => {
    if (!noteText.trim()) return;
    onAddNote(task.id, noteText.trim());
    setShowNoteForm(false);
    setNoteText('');
  };

  const handleConfirmReopen = () => {
    onReopen(task.id, reopenReason.trim());
    setShowReopenForm(false);
    setReopenReason('');
  };

  const theme = STATUS_THEME[task.status] || STATUS_THEME._default;
  const StatusIcon = STATUS_ICON_MAP[task.status] || Circle;
  const hasNotes = Boolean(task.evidenceNote);
  const hasReopenHistory = task._reopenHistory?.length > 0;
  const expandable = hasNotes || hasReopenHistory;

  return (
    <li className={`group relative overflow-hidden rounded-xl border ${theme.border} ${theme.bg} shadow-sm transition-all duration-200 hover:shadow-md`}>
      {/* Left status strip */}
      <div className={`absolute inset-y-0 left-0 w-1 ${theme.strip}`} />

      <div className="flex items-start gap-3 pl-4 pr-3.5 py-3.5">
        {/* Status icon with background circle */}
        <div className={`mt-0.5 flex shrink-0 items-center justify-center rounded-full ${theme.iconBg} p-1.5`}>
          <StatusIcon className={`${theme.iconSize} ${theme.iconText}`} aria-hidden />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className={`text-[13px] font-semibold leading-snug ${theme.titleColor}`}>
              {task.title}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {task.priority === 'critical' && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-red-500 to-rose-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  CRITICAL
                </span>
              )}
              {expandable && (
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                  title={expanded ? 'Collapse' : 'Show notes & history'}
                >
                  {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
          </div>

          {task.description && (
            <div className="mt-0.5 text-xs leading-relaxed text-slate-500 line-clamp-2">{task.description}</div>
          )}

          {/* Metadata badges */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            {blocked && task.blockedReason && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800 font-medium">
                <Lock className="h-2.5 w-2.5" />
                {task.blockedReason}
              </span>
            )}
            {task.ownerRole && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-600 font-medium">
                <UserCheck className="h-2.5 w-2.5" />
                {task.ownerRole}
              </span>
            )}
            {task.dueAt && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                overdue
                  ? 'bg-red-100 text-red-700'
                  : 'bg-slate-100 text-slate-500'
              }`}>
                <Clock className="h-2.5 w-2.5" />
                Due {task.dueAt}
              </span>
            )}
            {done && task.completedBy && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700 font-medium">
                <CheckCircle2 className="h-2.5 w-2.5" />
                {task.completedBy}
                {task.completedAt ? ` · ${new Date(task.completedAt).toLocaleDateString()}` : ''}
              </span>
            )}
            {hasNotes && !expanded && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-purple-500 font-medium">
                <MessageSquare className="h-2.5 w-2.5" />
                Has notes
              </span>
            )}
          </div>

          {/* Expanded notes & history */}
          {expanded && (
            <div className="mt-3 space-y-2">
              {hasNotes && (
                <div className="rounded-lg bg-white/80 border border-slate-200 px-3 py-2.5 text-xs text-slate-700 shadow-inner">
                  <p className="font-semibold text-slate-800 mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3 text-purple-500" />
                    Notes
                  </p>
                  <p className="whitespace-pre-line leading-relaxed text-slate-600">{task.evidenceNote}</p>
                </div>
              )}
              {hasReopenHistory && (
                <div className="rounded-lg bg-amber-50/80 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 shadow-inner">
                  <p className="font-semibold mb-1 flex items-center gap-1">
                    <Undo2 className="h-3 w-3 text-amber-600" />
                    Reopen history
                  </p>
                  {task._reopenHistory.map((h, i) => (
                    <p key={i} className="leading-relaxed text-amber-700">
                      {new Date(h.reopenedAt).toLocaleString()} — <span className="font-medium">{h.reopenedBy}</span>
                      {h.reason ? `: ${h.reason}` : ''}
                      {h.previousCompletedBy ? ` (prev. completed by ${h.previousCompletedBy})` : ''}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex shrink-0 items-center gap-1 pt-0.5">
          {isActionable && !showCompleteForm && (
            <>
              {task.status === TASK_STATUS.PENDING && (
                <button
                  type="button"
                  onClick={() => onStartProgress(task.id)}
                  className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 hover:border-blue-300 transition-all shadow-sm"
                  title="Start working"
                >
                  <Play className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCompleteForm(true)}
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-600 hover:bg-emerald-100 hover:border-emerald-300 transition-all shadow-sm"
                title="Complete with optional note"
                data-testid={`task-complete-${task.templateId}`}
              >
                <CheckCircle2 className="h-4 w-4" />
              </button>
            </>
          )}

          {(done || cancelled) && !showReopenForm && (
            <button
              type="button"
              onClick={() => setShowReopenForm(true)}
              className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-600 hover:bg-amber-100 hover:border-amber-300 transition-all shadow-sm"
              title="Undo — reopen this task"
            >
              <Undo2 className="h-4 w-4" />
            </button>
          )}

          {!showNoteForm && !showCompleteForm && !showReopenForm && (
            <button
              type="button"
              onClick={() => setShowNoteForm(true)}
              className="rounded-lg border border-transparent bg-slate-50 p-2 text-slate-400 hover:bg-purple-50 hover:text-purple-500 hover:border-purple-200 transition-all"
              title="Add note"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Complete with evidence note ────────────────────────── */}
      {showCompleteForm && (
        <div className="mx-4 mb-3.5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white p-4 shadow-inner">
          <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Mark complete — evidence note (optional)
          </p>
          <textarea
            value={evidenceNote}
            onChange={(e) => setEvidenceNote(e.target.value)}
            placeholder="What was done? Who was involved? Any follow-up needed?"
            rows={2}
            className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 resize-none"
            autoFocus
          />
          <div className="mt-2.5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowCompleteForm(false); setEvidenceNote(''); }}
              className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 shadow-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitComplete}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:from-emerald-600 hover:to-emerald-700 transition-all"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Complete
            </button>
          </div>
        </div>
      )}

      {/* ── Add note (without completing) ─────────────────────── */}
      {showNoteForm && (
        <div className="mx-4 mb-3.5 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50/80 to-white p-4 shadow-inner">
          <p className="text-xs font-bold text-purple-800 mb-2 flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />
            Add a note
          </p>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Progress update, concern, or follow-up…"
            rows={2}
            className="w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder-slate-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100 resize-none"
            autoFocus
          />
          <div className="mt-2.5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowNoteForm(false); setNoteText(''); }}
              className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 shadow-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitNote}
              disabled={!noteText.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-3.5 w-3.5" />
              Save Note
            </button>
          </div>
        </div>
      )}

      {/* ── Reopen confirmation ────────────────────────────────── */}
      {showReopenForm && (
        <div className="mx-4 mb-3.5 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/80 to-white p-4 shadow-inner">
          <p className="text-xs font-bold text-amber-800 mb-1 flex items-center gap-1.5">
            <Undo2 className="h-3.5 w-3.5" />
            Reopen this task?
          </p>
          <p className="text-[11px] text-amber-600 mb-2">
            Sets the task back to pending. The original completion is preserved in the audit trail.
          </p>
          <textarea
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            placeholder="Reason for reopening (optional but recommended)…"
            rows={2}
            className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder-amber-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 resize-none"
            autoFocus
          />
          <div className="mt-2.5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowReopenForm(false); setReopenReason(''); }}
              className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 shadow-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmReopen}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:from-amber-600 hover:to-amber-700 transition-all"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Reopen Task
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ── Main checklist component ────────────────────────────────────────────

export default function AdmissionTaskChecklist({
  residentId,
  facilityId,
  admissionDateIso,
  workflowAnchorIso,
  workflowKey,
  title,
  currentUserId = 'demo_user',
}) {
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((x) => x + 1), []);

  const anchor = useMemo(() => {
    if (workflowAnchorIso && /^\d{4}-\d{2}-\d{2}/.test(workflowAnchorIso)) {
      return workflowAnchorIso.split('T')[0];
    }
    if (admissionDateIso && /^\d{4}-\d{2}-\d{2}/.test(admissionDateIso)) {
      return admissionDateIso.split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  }, [admissionDateIso, workflowAnchorIso]);

  useEffect(() => {
    if (residentId == null || !facilityId || !workflowKey) return;
    const r = ensureWorkflow({
      residentId,
      facilityId,
      workflowKey,
      anchorDateIso: anchor,
      createdBy: currentUserId,
    });
    if (process.env.NODE_ENV === 'development' && r.created) {
      console.debug('[AdmissionTaskChecklist] workflow ensured', workflowKey, r.tasks?.length);
    }
    refresh();
  }, [residentId, facilityId, workflowKey, anchor, currentUserId, refresh]);

  const tasks = useMemo(() => {
    void tick;
    return listTasks(residentId, workflowKey);
  }, [residentId, workflowKey, tick]);

  const progress = useMemo(
    () => completionPercent(residentId, workflowKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [residentId, workflowKey, tick]
  );

  const acer = useMemo(() => {
    if (workflowKey !== WORKFLOW_KEYS.STAGE6_ADMISSION) return null;
    try {
      return acerSlaFromAdmission(anchor);
    } catch {
      return null;
    }
  }, [workflowKey, anchor]);

  // ── Handlers ────────────────────────────────────────────────────────

  const handleComplete = useCallback(
    (taskId, note) => {
      completeTask(residentId, taskId, currentUserId, note);
      refresh();
    },
    [residentId, currentUserId, refresh]
  );

  const handleStartProgress = useCallback(
    (taskId) => {
      setTaskInProgress(residentId, taskId);
      refresh();
    },
    [residentId, refresh]
  );

  const handleReopen = useCallback(
    (taskId, reason) => {
      reopenTask(residentId, taskId, currentUserId, reason);
      refresh();
    },
    [residentId, currentUserId, refresh]
  );

  const handleAddNote = useCallback(
    (taskId, note) => {
      addTaskNote(residentId, taskId, note, currentUserId);
      refresh();
    },
    [residentId, currentUserId, refresh]
  );

  if (!workflowKey) return null;

  const barColor = progress.percent >= 100
    ? 'bg-emerald-500'
    : progress.percent >= 60
      ? 'bg-gradient-to-r from-emerald-500 to-emerald-400'
      : progress.percent >= 30
        ? 'bg-gradient-to-r from-blue-500 to-blue-400'
        : 'bg-slate-400';

  const doneCount = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
  const overdueCount = tasks.filter((t) => t.status === TASK_STATUS.OVERDUE).length;
  const inProgressCount = tasks.filter(
    (t) => t.status === TASK_STATUS.IN_PROGRESS
  ).length;

  return (
    <div
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      data-testid={`admission-checklist-${workflowKey}`}
    >
      {/* ── Gradient header ────────────────────────────────── */}
      <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 px-5 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
              <ClipboardList className="h-5 w-5 text-white" aria-hidden />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">{title || 'Operational Tasks'}</h4>
              <p className="text-xs text-emerald-100">
                Anchor: <span className="font-mono text-white/80">{anchor}</span>
                {workflowAnchorIso ? ' · Enquiry pipeline' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Mini stat pills */}
            <div className="hidden sm:flex items-center gap-2">
              {inProgressCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/20 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold text-white">
                  <Play className="h-3 w-3" />
                  {inProgressCount} active
                </span>
              )}
              {overdueCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-500/30 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold text-white">
                  <AlertTriangle className="h-3 w-3" />
                  {overdueCount} overdue
                </span>
              )}
            </div>

            {/* Progress ring */}
            <div className="flex items-center gap-3">
              <div className="relative h-14 w-14">
                <svg className="h-14 w-14 -rotate-90" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                  <circle
                    cx="28"
                    cy="28"
                    r="24"
                    fill="none"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${(progress.percent / 100) * 150.8} 150.8`}
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-sm font-black text-white leading-none">{progress.percent}%</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-white">
                  {doneCount} of {progress.total}
                </div>
                <div className="text-[11px] text-emerald-100">completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────── */}
      <div className="p-4">
        {/* ACER SLA banner */}
        {acer && (
          <div
            className={`mb-4 flex items-center gap-2.5 rounded-xl border px-4 py-3 text-xs ${
              acer.alertLevel === 'critical'
                ? 'border-red-200 bg-gradient-to-r from-red-50 to-white text-red-900'
                : acer.alertLevel === 'warning'
                  ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-white text-amber-900'
                  : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
              acer.alertLevel === 'critical' ? 'bg-red-100' : acer.alertLevel === 'warning' ? 'bg-amber-100' : 'bg-slate-100'
            }`}>
              <Clock className={`h-4 w-4 ${
                acer.alertLevel === 'critical' ? 'text-red-600' : acer.alertLevel === 'warning' ? 'text-amber-600' : 'text-slate-500'
              }`} />
            </div>
            <div>
              <span className="font-bold">ACER SLA (28-day window): </span>
              {acer.daysUntilAcerDue < 0
                ? `Overdue by ${Math.abs(acer.daysUntilAcerDue)} day(s) — urgent follow-up required.`
                : `${acer.daysUntilAcerDue} day(s) remaining until day-28 ACER due date.`}
            </div>
          </div>
        )}

        {/* Task list */}
        <ul className="space-y-2.5">
          {tasks.length === 0 ? (
            <li className="flex flex-col items-center justify-center gap-2 py-10 text-sm text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" aria-hidden />
              <span>Loading tasks…</span>
            </li>
          ) : (
            tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                currentUserId={currentUserId}
                onComplete={handleComplete}
                onStartProgress={handleStartProgress}
                onReopen={handleReopen}
                onAddNote={handleAddNote}
              />
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
